/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LeaseService } from '../services/lease-service';
import { leasesKeys } from './leases-keys';
import type { Lease } from '../types/lease.types';

export interface Leases {
  isLoading: boolean;
  isError: boolean;
  leases: Lease[];
  refresh: () => void;
}

/** Rows per request; the endpoint's own maximum page size. */
const PAGE_SIZE = 50;

/**
 * Fetches every page of the registry.
 *
 * The first response reports how many pages exist, so the rest are
 * requested by number rather than by walking `next` — no unbounded loop,
 * and the count is known before any extra request is made.
 *
 * Reading the whole registry is deliberate: the filter panel, the search
 * box and the sortable headers all work over the full set, and paging on
 * the server would quietly reduce them to "filter this page", which is a
 * subtly wrong answer rather than a slow one.
 */
async function fetchAllLeases(agencyId: string, overdue = false): Promise<Lease[]> {
  const first = await LeaseService.listLeases(agencyId, undefined, 1, PAGE_SIZE, overdue);
  const totalPages = Math.max(1, first.pages ?? 1);
  if (totalPages === 1) return first.data ?? [];

  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      LeaseService.listLeases(agencyId, undefined, i + 2, PAGE_SIZE, overdue)),
  );
  return [...(first.data ?? []), ...rest.flatMap((page) => page.data ?? [])];
}

/**
 * Loads the agency's leases.
 *
 * @param agencyId - The agency whose leases to load.
 * @param overdue - Narrow to the contracts owing rent. Scanned server-side,
 *   so it is a separate query rather than a filter over the loaded rows.
 * @returns The view-model: flags, rows and a refresh action.
 */
export function useLeases(agencyId: string, overdue = false): Leases {
  const queryClient = useQueryClient();

  const allQuery = useQuery({
    queryKey: leasesKeys.list(agencyId, overdue ? 'overdue' : ''),
    queryFn: () => fetchAllLeases(agencyId, overdue),
    enabled: Boolean(agencyId),
  });

  return {
    isLoading: allQuery.isLoading,
    isError: allQuery.isError,
    leases: allQuery.data ?? [],
    refresh: () => queryClient.invalidateQueries({ queryKey: leasesKeys.all(agencyId) }),
  };
}
